import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Users, Handshake, Package, CurrencyEur, TrendUp, Phone, Envelope, ChartLineUp, CaretLeft, CaretRight, Calendar } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, Legend, ReferenceLine, Area, ComposedChart, LabelList } from 'recharts';
import { PersonLink } from '@/components/PersonLink';
import WhatsAppLink from '@/components/WhatsAppLink';
import useDashboardProjection from '@/hooks/useDashboardProjection';
import ObjetivoMesBanner from './dashboard/ObjetivoMesBanner';

const MONTHS = ['', 'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

function StatCard({ icon: Icon, label, value, color, onClick, testId, hint }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className="bg-white border border-[#E5E7EB] rounded-lg p-3 sm:p-4 flex flex-col gap-2 sm:gap-2.5 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200 text-left w-full min-w-0">
      <div className="flex items-center gap-2 min-w-0">
        <span className="p-1.5 sm:p-2 rounded-md flex-shrink-0" style={{ backgroundColor: `${color}12` }}>
          <Icon size={16} weight="duotone" style={{ color }} />
        </span>
        <p className="text-[9px] sm:text-[11px] uppercase tracking-[0.12em] sm:tracking-[0.15em] font-medium text-[#6B7280] flex-1 min-w-0 truncate flex items-center gap-1.5">
          <span className="truncate">{label}</span>
          {hint && (
            <span
              role="tooltip"
              title={hint}
              onClick={(e) => e.stopPropagation()}
              data-testid={testId ? `${testId}-hint` : undefined}
              className="inline-flex flex-shrink-0 items-center justify-center w-3.5 h-3.5 rounded-full bg-[#E5E7EB] text-[#6B7280] text-[9px] font-bold cursor-help hover:bg-[#D1D5DB] hover:text-[#111827] transition-colors normal-case tracking-normal"
              aria-label="Como é calculado"
            >
              ?
            </span>
          )}
        </p>
      </div>
      <p
        title={value}
        className="text-[15px] sm:text-base lg:text-lg xl:text-xl font-bold text-[#111827] whitespace-nowrap overflow-hidden text-ellipsis tabular-nums"
        style={{ fontFamily: "'JetBrains Mono', monospace" }}
      >{value}</p>
    </button>
  );
}

function ContratoCard({ fatContrato, fmt }) {
  if (!fatContrato) return null;
  if (!fatContrato.hasContract) {
    return (
      <div data-testid="card-fat-contrato-empty" className="bg-white border border-[#E5E7EB] rounded-lg p-4 mb-6 text-sm text-[#6B7280]">
        <span className="inline-flex items-center gap-2"><Calendar size={16} /> Sem data de renovação de contrato de consultor registada.</span>
      </div>
    );
  }
  const { cycleStart, cycleEnd, faturacaoPessoal, faturacaoClientes, faturacaoRede, total } = fatContrato;
  const clientesValue = faturacaoClientes ?? faturacaoRede ?? 0;
  const start = cycleStart ? new Date(cycleStart).toLocaleDateString('pt-PT') : '-';
  const end = cycleEnd ? new Date(cycleEnd).toLocaleDateString('pt-PT') : '-';
  const endDate = cycleEnd ? new Date(cycleEnd) : null;
  const daysLeft = endDate ? Math.max(0, Math.ceil((endDate - new Date()) / (1000 * 60 * 60 * 24))) : null;
  return (
    <div data-testid="card-fat-contrato" className="bg-white border border-[#E5E7EB] rounded-lg p-5 mb-6">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-3">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-md" style={{ backgroundColor: '#E6510012' }}>
            <Calendar size={20} style={{ color: '#E65100' }} />
          </div>
          <div>
            <p className="text-sm font-semibold text-[#111827] flex items-center gap-1.5" style={{ fontFamily: "'Fraunces', sans-serif" }}>
              Volume de Faturação no Contrato de Consultor
              <span
                title="Mesma fórmula da vFacturacaoAno do legacy: Faturação Pessoal + Faturação Clientes directos (ID_ClienteRecomendacao=consultor), excluindo talentos/downline (CS/CT) e produtos resell-lead. Filtra por DataPagamento no intervalo do ciclo."
                className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-[#E5E7EB] text-[#6B7280] text-[9px] font-bold cursor-help hover:bg-[#D1D5DB]">?</span>
            </p>
            <p className="text-xs text-[#6B7280] mt-0.5">Ciclo actual: <span className="font-mono">{start} → {end}</span>{daysLeft !== null && <> · <span className="text-[#E65100] font-medium">{daysLeft} dias até à renovação</span></>}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280]">Total Contratual</p>
          <p className="text-2xl font-bold text-[#111827]" style={{ fontFamily: "'JetBrains Mono', monospace" }} data-testid="fat-contrato-total">{fmt(total)}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
        <div className="border border-[#E5E7EB] rounded-md p-3">
          <p className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B7280]">Faturação Pessoal</p>
          <p className="text-lg font-bold text-[#111827] mt-0.5" style={{ fontFamily: "'JetBrains Mono', monospace" }} data-testid="fat-contrato-pessoal">{fmt(faturacaoPessoal)}</p>
        </div>
        <div className="border border-[#E5E7EB] rounded-md p-3">
          <p className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B7280]">Faturação Clientes Directos</p>
          <p className="text-lg font-bold text-[#111827] mt-0.5" style={{ fontFamily: "'JetBrains Mono', monospace" }} data-testid="fat-contrato-clientes">{fmt(clientesValue)}</p>
        </div>
      </div>
    </div>
  );
}



export default function DashboardPage() {
  const [fatMes, setFatMes] = useState(0);
  const [fatAno, setFatAno] = useState(0);
  const [fatContrato, setFatContrato] = useState(null);
  const [comparativo, setComparativo] = useState(null);
  const [birthdays, setBirthdays] = useState([]);
  const [bdPage, setBdPage] = useState(1);
  const BD_PER_PAGE = 10;
  const [downlines, setDownlines] = useState([]);
  const [comDisp, setComDisp] = useState({ disponivel: 0, receber: 0 });
  const [compFat, setCompFat] = useState(null);
  const [compFatPessoal, setCompFatPessoal] = useState(null);
  const [projIndiv, setProjIndiv] = useState([]);
  const [nextCourses, setNextCourses] = useState([]);
  const [latestInscriptions, setLatestInscriptions] = useState([]);
  const [latestRegisters, setLatestRegisters] = useState([]);
  const [objetivoMes, setObjetivoMes] = useState(null);
  const [registosSemanais, setRegistosSemanais] = useState([]);
  const [registosTotais, setRegistosTotais] = useState({ totalAteFimAnoAnterior: 0, totalActual: 0 });
  const [registosMensais, setRegistosMensais] = useState([]);
  const [registosMensaisCurrent, setRegistosMensaisCurrent] = useState({ ano: 0, mes: 0, diretos: 0, rede: 0, total: 0 });
  const [clientesResumo, setClientesResumo] = useState({
    directos: { total: 0, compradores: 0, pctCompradores: 0, volumeVendas: 0, volumeMedio: 0 },
    rede: { total: 0, compradores: 0, pctCompradores: 0, volumeVendas: 0, volumeMedio: 0 },
  });
  const [clientesEvolucao, setClientesEvolucao] = useState([]);
  const navigate = useNavigate();
  const { activeId } = useAuth();
  const fmt = (v) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(v || 0);

  useEffect(() => {
    api.post('/dashboard/faturacaoMesCorrente').then(r => { const d = r.data; setFatMes(Array.isArray(d) && d[0] ? d[0].total : 0); }).catch(() => {});
    api.post('/dashboard/faturacaoAcumulada').then(r => { const d = r.data; setFatAno(Array.isArray(d) && d[0] ? d[0].total : 0); }).catch(() => {});
    api.post('/dashboard/faturacaoContrato').then(r => setFatContrato(r.data || null)).catch(() => {});
    api.post('/dashboard/comparativoAnual').then(r => setComparativo(r.data)).catch(() => {});
    api.post('/dashboard/comparativoFaturacao').then(r => setCompFat(r.data)).catch(() => {});
    api.post('/dashboard/comparativoFaturacaoPessoal').then(r => setCompFatPessoal(r.data)).catch(() => {});
    api.post('/dashboard/birthdays').then(r => setBirthdays(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.post('/dashboard/downlinesExpiring').then(r => setDownlines(r.data?.data || [])).catch(() => {});
    api.post('/dashboard/comissoesDisponiveis').then(r => setComDisp(r.data || { disponivel: 0, receber: 0 })).catch(() => {});
    api.post('/dashboard/projecaoIndividual').then(r => setProjIndiv(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/dashboard/nextCourses').then(r => setNextCourses(r.data?.data || [])).catch(() => {});
    api.get('/dashboard/latestInscriptions').then(r => setLatestInscriptions(r.data?.data || [])).catch(() => {});
    api.get('/dashboard/latestRegisters').then(r => setLatestRegisters(r.data?.data || [])).catch(() => {});
    api.get('/dashboard/currentForecast').then(r => setObjetivoMes(r.data?.data || null)).catch(() => {});
    api.post('/dashboard/registosDiretosSemanais').then(r => {
      setRegistosSemanais(r.data?.items || []);
      setRegistosTotais({
        totalAteFimAnoAnterior: r.data?.totalAteFimAnoAnterior || 0,
        totalActual: r.data?.totalActual || 0,
      });
    }).catch(() => {});
    api.post('/dashboard/registosMensais').then(r => {
      setRegistosMensais(r.data?.items || []);
      setRegistosMensaisCurrent(r.data?.currentMonth || { ano: 0, mes: 0, diretos: 0, rede: 0, total: 0 });
    }).catch(() => {});
    api.post('/dashboard/clientesResumo').then(r => {
      setClientesResumo(r.data || {
        directos: { total: 0, compradores: 0, pctCompradores: 0, volumeVendas: 0, volumeMedio: 0 },
        rede: { total: 0, compradores: 0, pctCompradores: 0, volumeVendas: 0, volumeMedio: 0 },
      });
    }).catch(() => {});
    api.post('/dashboard/clientesEvolucao').then(r => {
      setClientesEvolucao(r.data?.items || []);
    }).catch(() => {});
  }, [activeId]);

  // Derive all chart data + KPIs via reusable hook
  const { chartData, fatChartData, fatPessoalChartData, projChartData, projectedTotal, projectedIndivTotal,
          actualYTD, lastYearTotal, growthPct, growthPctIndiv, currentYear } =
    useDashboardProjection({ comparativo, compFat, compFatPessoal, projIndiv });

  // Registos diretos semanais (ano atual vs mesma semana ano anterior)
  const registosChartData = (registosSemanais || []).map((r) => ({
    label: `S${String(r.Semana).padStart(2, '0')}`,
    semana: r.Semana,
    'Ano Atual': r.Atual || 0,
    'Ano Anterior': r.Anterior || 0,
  }));
  // Determina a última semana com dados no ano corrente para fazer comparação YTD apples-to-apples
  const ultimaSemanaAtual = registosChartData.reduce((max, r) => r['Ano Atual'] > 0 ? Math.max(max, r.semana) : max, 0);
  const registosTotalAtual = registosChartData.reduce((s, r) => s + r['Ano Atual'], 0);
  const registosTotalAnteriorPleno = registosChartData.reduce((s, r) => s + r['Ano Anterior'], 0);
  const registosTotalAnteriorYTD = registosChartData
    .filter((r) => ultimaSemanaAtual === 0 || r.semana <= ultimaSemanaAtual)
    .reduce((s, r) => s + r['Ano Anterior'], 0);
  const registosDelta = registosTotalAnteriorYTD > 0
    ? ((registosTotalAtual - registosTotalAnteriorYTD) / registosTotalAnteriorYTD) * 100
    : (registosTotalAtual > 0 ? 100 : 0);

  // Registos mensais (24 meses) — separar corrente + ano anterior alinhados por mês
  const now = new Date();
  const registosMensaisChartData = [];
  const findRegisto = (ano, mes) => (registosMensais || []).find(x => x.ano === ano && x.mes === mes);
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const yy = d.getFullYear();
    const mm = d.getMonth() + 1;
    const cur = findRegisto(yy, mm);
    const prev = findRegisto(yy - 1, mm);
    registosMensaisChartData.push({
      label: `${MONTHS[mm]}${yy !== currentYear ? '/' + String(yy).slice(2) : ''}`,
      ano: yy, mes: mm,
      Diretos: cur?.diretos || 0,
      Rede: cur?.rede || 0,
      Total: cur?.total || 0,
      TotalAnoAnt: prev?.total || 0,
      DiretosAnoAnt: prev?.diretos || 0,
      RedeAnoAnt: prev?.rede || 0,
    });
  }

  // Evolução mensal — compradores % e ticket médio (YoY 12 vs 12)
  const findEvo = (ano, mes) => (clientesEvolucao || []).find(x => x.ano === ano && x.mes === mes);
  const evolucaoChartData = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const yy = d.getFullYear();
    const mm = d.getMonth() + 1;
    const cur = findEvo(yy, mm) || {};
    const prev = findEvo(yy - 1, mm) || {};
    evolucaoChartData.push({
      label: `${MONTHS[mm]}${yy !== currentYear ? '/' + String(yy).slice(2) : ''}`,
      ano: yy, mes: mm,
      DirPct: cur.dirPctCompradores || 0,
      DirPctPrev: prev.dirPctCompradores || 0,
      RedePct: cur.redePctCompradores || 0,
      RedePctPrev: prev.redePctCompradores || 0,
      DirTicket: cur.dirTicket || 0,
      DirTicketPrev: prev.dirTicket || 0,
      RedeTicket: cur.redeTicket || 0,
      RedeTicketPrev: prev.redeTicket || 0,
    });
  }

  return (
    <div data-testid="dashboard" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-[#111827]" style={{ fontFamily: "'Fraunces', sans-serif" }}>Vista Geral</h1>
        <p className="text-sm text-[#6B7280] mt-1">Dados em tempo real do SQL Server IHTP</p>
      </div>

      <ObjetivoMesBanner objetivoMes={objetivoMes} fatMes={fatMes} />

      <div className="grid grid-cols-2 lg:grid-cols-7 gap-3 sm:gap-4 mb-6">
        <StatCard icon={CurrencyEur} label="Faturação Mês" value={fmt(fatMes)} color="#0F1829" onClick={() => navigate('/encomendas')} testId="stat-fat-mes" />
        <StatCard icon={TrendUp} label="Faturação Ano" value={fmt(fatAno)} color="#00C853" onClick={() => navigate('/encomendas')} testId="stat-fat-ano" />
        <StatCard icon={ChartLineUp} label={`Proj. Rede Dez ${currentYear}`} value={fmt(projectedTotal)} color="#E65100" onClick={() => navigate('/projecao')} testId="stat-projecao"
          hint={`Projeta a faturação da rede como um bloco único. Aplica o factor de crescimento global (YTD ${currentYear} / mesmos meses ${currentYear - 1}) à faturação restante do ano anterior. Estável e menos sensível a outliers.`} />
        <StatCard icon={ChartLineUp} label={`Proj. Individual Dez ${currentYear}`} value={fmt(projectedIndivTotal)} color="#978D78" onClick={() => navigate('/projecao')} testId="stat-projecao-indiv"
          hint={`Soma das projeções calculadas consultor a consultor. Para cada um aplica o seu próprio factor de crescimento (YTD ${currentYear} / ${currentYear - 1}). Mais realista mas sensível a consultores novos sem histórico.`} />
        <StatCard icon={CurrencyEur} label="Comissões a Receber" value={fmt(comDisp.receber)} color="#6B7280" onClick={() => navigate('/comissoes')} testId="stat-com-receber" />
        <StatCard icon={Users} label="Registos Directos Mês" value={String(registosMensaisCurrent.diretos || 0)} color="#00C853" onClick={() => navigate('/rede')} testId="stat-reg-diretos-mes"
          hint="Clientes registados este mês em que o próprio consultor é o sponsor directo (ID_ClienteRecomendacao). Actualiza em tempo real." />
        <StatCard icon={Handshake} label="Registos Rede Mês" value={String(registosMensaisCurrent.rede || 0)} color="#E65100" onClick={() => navigate('/rede')} testId="stat-reg-rede-mes"
          hint="Clientes registados este mês por consultores da downline (rede). Exclui os directos para evitar dupla contagem." />
      </div>

      {/* Volume de Faturação no Ciclo Contratual */}
      <ContratoCard fatContrato={fatContrato} fmt={fmt} />

      {/* Compradores & volume médio — directos vs rede */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6" data-testid="clientes-resumo-grid">
        <div className="bg-gradient-to-br from-[#F0FDF4] via-white to-[#ECFDF5] border border-[#BBF7D0] rounded-lg p-4 shadow-sm" data-testid="kpi-pct-compradores-directos">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-[10px] uppercase tracking-widest text-[#065F46] font-semibold">% Compradores · Directos</p>
            <span className="text-[10px] text-[#00C853]">●</span>
          </div>
          <p className="text-2xl font-black text-[#0F1829] tabular-nums leading-tight">
            {(clientesResumo.directos.pctCompradores || 0).toFixed(1)}<span className="text-sm ml-0.5 text-[#6B7280]">%</span>
          </p>
          <p className="text-[11px] text-[#4B5563] mt-1 tabular-nums">
            {clientesResumo.directos.compradores} de {clientesResumo.directos.total} clientes
          </p>
        </div>
        <div className="bg-gradient-to-br from-[#FFF7ED] via-white to-[#FFEDD5] border border-[#FED7AA] rounded-lg p-4 shadow-sm" data-testid="kpi-pct-compradores-rede">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-[10px] uppercase tracking-widest text-[#9A3412] font-semibold">% Compradores · Rede</p>
            <span className="text-[10px] text-[#E65100]">●</span>
          </div>
          <p className="text-2xl font-black text-[#0F1829] tabular-nums leading-tight">
            {(clientesResumo.rede.pctCompradores || 0).toFixed(1)}<span className="text-sm ml-0.5 text-[#6B7280]">%</span>
          </p>
          <p className="text-[11px] text-[#4B5563] mt-1 tabular-nums">
            {clientesResumo.rede.compradores} de {clientesResumo.rede.total} clientes
          </p>
        </div>
        <div className="bg-gradient-to-br from-[#F0FDF4] via-white to-[#ECFDF5] border border-[#BBF7D0] rounded-lg p-4 shadow-sm" data-testid="kpi-volume-medio-directos">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-[10px] uppercase tracking-widest text-[#065F46] font-semibold">Volume Médio Vendas · Directos</p>
            <span className="text-[10px] text-[#00C853]">●</span>
          </div>
          <p className="text-2xl font-black text-[#0F1829] tabular-nums leading-tight">
            {fmt(clientesResumo.directos.volumeMedio)}
          </p>
          <p className="text-[11px] text-[#4B5563] mt-1 tabular-nums">
            Total vendas: {fmt(clientesResumo.directos.volumeVendas)}
          </p>
        </div>
        <div className="bg-gradient-to-br from-[#FFF7ED] via-white to-[#FFEDD5] border border-[#FED7AA] rounded-lg p-4 shadow-sm" data-testid="kpi-volume-medio-rede">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-[10px] uppercase tracking-widest text-[#9A3412] font-semibold">Volume Médio Negócio · Rede</p>
            <span className="text-[10px] text-[#E65100]">●</span>
          </div>
          <p className="text-2xl font-black text-[#0F1829] tabular-nums leading-tight">
            {fmt(clientesResumo.rede.volumeMedio)}
          </p>
          <p className="text-[11px] text-[#4B5563] mt-1 tabular-nums">
            Total negócio: {fmt(clientesResumo.rede.volumeVendas)}
          </p>
        </div>
      </div>

      {/* Evolução Compradores & Ticket Médio — Directos e Rede vs Ano Anterior */}
      {evolucaoChartData.some(r => r.DirTicket > 0 || r.RedeTicket > 0 || r.DirPct > 0 || r.RedePct > 0 || r.DirTicketPrev > 0 || r.RedeTicketPrev > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {/* % Compradores */}
          <div data-testid="chart-evolucao-compradores" className="bg-white border border-[#E5E7EB] rounded-lg p-5">
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-[#111827]">% Compradores por Mês — Directos vs Rede</h3>
              <p className="text-xs text-[#6B7280] mt-0.5">
                Clientes que compraram em cada mês, relativos ao universo total actual · linhas tracejadas = ano anterior
              </p>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={evolucaoChartData} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} tickFormatter={(v) => `${v}%`} />
                <Tooltip
                  contentStyle={{ background: '#0F1829', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12 }}
                  labelStyle={{ color: '#9CA3AF', fontSize: 11, textTransform: 'uppercase' }}
                  formatter={(v, n) => [`${(v || 0).toFixed(2)}%`, n]}
                />
                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 4 }} iconType="circle" />
                <Line type="monotone" dataKey="DirPct" name={`Directos ${currentYear}`} stroke="#00C853" strokeWidth={2.4} dot={{ r: 3, fill: '#00C853' }} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="DirPctPrev" name={`Directos ${currentYear - 1}`} stroke="#2563EB" strokeWidth={1.8} strokeDasharray="4 4" dot={{ r: 2, fill: '#2563EB', fillOpacity: 0.6 }} strokeOpacity={0.75} />
                <Line type="monotone" dataKey="RedePct" name={`Rede ${currentYear}`} stroke="#E65100" strokeWidth={2.4} dot={{ r: 3, fill: '#E65100' }} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="RedePctPrev" name={`Rede ${currentYear - 1}`} stroke="#E65100" strokeWidth={1.8} strokeDasharray="4 4" dot={{ r: 2, fill: '#E65100', fillOpacity: 0.4 }} strokeOpacity={0.55} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          {/* Ticket Médio */}
          <div data-testid="chart-evolucao-ticket" className="bg-white border border-[#E5E7EB] rounded-lg p-5">
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-[#111827]">Ticket Médio por Mês — Directos vs Rede</h3>
              <p className="text-xs text-[#6B7280] mt-0.5">
                Valor médio de compra por comprador em cada mês · linhas tracejadas = ano anterior
              </p>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={evolucaoChartData} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} tickFormatter={(v) => `${Math.round(v)}€`} />
                <Tooltip
                  contentStyle={{ background: '#0F1829', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12 }}
                  labelStyle={{ color: '#9CA3AF', fontSize: 11, textTransform: 'uppercase' }}
                  formatter={(v, n) => [fmt(v || 0), n]}
                />
                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 4 }} iconType="circle" />
                <Line type="monotone" dataKey="DirTicket" name={`Directos ${currentYear}`} stroke="#00C853" strokeWidth={2.4} dot={{ r: 3, fill: '#00C853' }} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="DirTicketPrev" name={`Directos ${currentYear - 1}`} stroke="#2563EB" strokeWidth={1.8} strokeDasharray="4 4" dot={{ r: 2, fill: '#2563EB', fillOpacity: 0.6 }} strokeOpacity={0.75} />
                <Line type="monotone" dataKey="RedeTicket" name={`Rede ${currentYear}`} stroke="#E65100" strokeWidth={2.4} dot={{ r: 3, fill: '#E65100' }} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="RedeTicketPrev" name={`Rede ${currentYear - 1}`} stroke="#E65100" strokeWidth={1.8} strokeDasharray="4 4" dot={{ r: 2, fill: '#E65100', fillOpacity: 0.4 }} strokeOpacity={0.55} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Registos por Mês — directos vs rede (últimos 12 meses) */}
      {registosMensaisChartData.some(r => r.Total > 0) && (
        <div data-testid="chart-registos-mensais" className="bg-white border border-[#E5E7EB] rounded-lg p-5 mb-6">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div>
              <h3 className="text-sm font-semibold text-[#111827]">Registos por Mês — Directos vs Rede</h3>
              <p className="text-xs text-[#6B7280] mt-0.5">
                Últimos 12 meses · verde = você é o sponsor directo · laranja = registos feitos pela sua downline · linha tracejada = total do ano anterior
              </p>
            </div>
            <div className="flex items-center gap-5 text-right">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[#6B7280]">Directos 12m</p>
                <p className="text-lg font-bold text-[#00C853] tabular-nums" data-testid="reg-mes-total-diretos-12m">
                  {registosMensaisChartData.reduce((s, r) => s + r.Diretos, 0)}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[#6B7280]">Rede 12m</p>
                <p className="text-lg font-bold text-[#E65100] tabular-nums" data-testid="reg-mes-total-rede-12m">
                  {registosMensaisChartData.reduce((s, r) => s + r.Rede, 0)}
                </p>
              </div>
              <div className="border-l border-[#E5E7EB] pl-5">
                <p className="text-[10px] uppercase tracking-widest text-[#6B7280]">Total 12m</p>
                <p className="text-xl font-black text-[#0F1829] tabular-nums" data-testid="reg-mes-total-12m">
                  {registosMensaisChartData.reduce((s, r) => s + r.Total, 0)}
                </p>
              </div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={registosMensaisChartData} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} allowDecimals={false} />
              <Tooltip
                cursor={{ fill: '#F3F4F6' }}
                contentStyle={{ background: '#0F1829', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12 }}
                labelStyle={{ color: '#9CA3AF', fontSize: 11, textTransform: 'uppercase' }}
              />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 4 }} iconType="circle" />
              <Bar dataKey="Diretos" stackId="a" fill="#00C853" name="Directos" radius={[0, 0, 0, 0]} />
              <Bar dataKey="Rede" stackId="a" fill="#E65100" name="Rede" radius={[4, 4, 0, 0]}>
                <LabelList
                  dataKey="Total"
                  position="top"
                  formatter={(v) => (v > 0 ? v : '')}
                  style={{ fontSize: 11, fill: '#0F1829', fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}
                />
              </Bar>
              <Line
                type="monotone"
                dataKey="TotalAnoAnt"
                name={`Total ${currentYear - 1}`}
                stroke="#6B7280"
                strokeWidth={2}
                strokeDasharray="4 4"
                dot={{ r: 3, fill: '#6B7280' }}
                activeDot={{ r: 5 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Registos Directos por Semana — comparativo ano vs ano anterior */}
      {registosChartData.length > 0 && (registosTotalAtual > 0 || registosTotalAnteriorPleno > 0 || registosTotais.totalActual > 0) && (
        <div data-testid="chart-registos-diretos" className="bg-white border border-[#E5E7EB] rounded-lg p-5 mb-6">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div>
              <h3 className="text-sm font-semibold text-[#111827]">Registos Directos por Semana</h3>
              <p className="text-xs text-[#6B7280] mt-0.5">
                Clientes registados consigo como sponsor directo · todas as semanas {currentYear} vs {currentYear - 1}
              </p>
            </div>
            <div className="flex items-center gap-5 text-right">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[#6B7280]">YTD {currentYear}</p>
                <p className="text-lg font-bold text-[#0F1829] tabular-nums" data-testid="registos-total-atual">{registosTotalAtual}</p>
                {ultimaSemanaAtual > 0 && (
                  <p className="text-[9px] text-[#9CA3AF] uppercase">até S{String(ultimaSemanaAtual).padStart(2, '0')}</p>
                )}
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[#6B7280]">{currentYear - 1} mesmo período</p>
                <p className="text-lg font-bold text-[#9CA3AF] tabular-nums" data-testid="registos-total-anterior-ytd">{registosTotalAnteriorYTD}</p>
                {ultimaSemanaAtual > 0 && (
                  <p className="text-[9px] text-[#9CA3AF] uppercase">até S{String(ultimaSemanaAtual).padStart(2, '0')}</p>
                )}
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[#6B7280]">Variação YTD</p>
                <p className={`text-lg font-bold tabular-nums ${registosDelta >= 0 ? 'text-[#00C853]' : 'text-[#FF2B2B]'}`} data-testid="registos-delta">
                  {registosDelta >= 0 ? '+' : ''}{registosDelta.toFixed(1)}%
                </p>
              </div>
              <div className="border-l border-[#E5E7EB] pl-5">
                <p className="text-[10px] uppercase tracking-widest text-[#6B7280]">{currentYear - 1} ano completo</p>
                <p className="text-base font-bold text-[#9CA3AF] tabular-nums" data-testid="registos-total-anterior-pleno">{registosTotalAnteriorPleno}</p>
              </div>
            </div>
          </div>

          {/* Rede Directa Total — snapshot fim do ano passado vs hoje */}
          {registosTotais.totalActual > 0 && (
            <div className="mb-4 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="registos-totais-rede">
              <div className="bg-[#FAF8F2] border border-[#E6E1D6] rounded-md px-4 py-3">
                <p className="text-[10px] uppercase tracking-widest text-[#978D78] font-bold">Rede directa · fim de {currentYear - 1}</p>
                <p className="text-2xl font-black text-[#0F1829] tabular-nums" data-testid="registos-total-fim-ano-anterior">
                  {registosTotais.totalAteFimAnoAnterior.toLocaleString('pt-PT')}
                </p>
                <p className="text-[10px] text-[#6B7280] mt-0.5">Clientes acumulados em 31/12/{currentYear - 1}</p>
              </div>
              <a href="/rede" data-testid="registos-link-rede"
                 className="bg-[#0F1829] text-white rounded-md px-4 py-3 hover:bg-black transition-colors group">
                <p className="text-[10px] uppercase tracking-widest text-[#9CA3AF] font-bold">Rede directa · agora</p>
                <p className="text-2xl font-black tabular-nums" data-testid="registos-total-actual">
                  {registosTotais.totalActual.toLocaleString('pt-PT')}
                </p>
                <p className="text-[10px] text-[#9CA3AF] mt-0.5 group-hover:text-white transition-colors">Ver rede completa →</p>
              </a>
              <div className="bg-[#FAF8F2] border border-[#E6E1D6] rounded-md px-4 py-3">
                <p className="text-[10px] uppercase tracking-widest text-[#978D78] font-bold">Crescimento {currentYear}</p>
                {(() => {
                  const delta = registosTotais.totalActual - registosTotais.totalAteFimAnoAnterior;
                  const pct = registosTotais.totalAteFimAnoAnterior > 0
                    ? (delta / registosTotais.totalAteFimAnoAnterior) * 100
                    : 0;
                  return (
                    <>
                      <p className={`text-2xl font-black tabular-nums ${delta >= 0 ? 'text-[#00C853]' : 'text-[#FF2B2B]'}`} data-testid="registos-crescimento-ytd">
                        {delta >= 0 ? '+' : ''}{delta.toLocaleString('pt-PT')}
                      </p>
                      <p className="text-[10px] text-[#6B7280] mt-0.5">{pct >= 0 ? '+' : ''}{pct.toFixed(2)}% vs 31/12/{currentYear - 1}</p>
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={registosChartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#6B7280' }} interval={2} angle={-45} textAnchor="end" height={45} />
                <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} allowDecimals={false} />
                {ultimaSemanaAtual > 0 && (
                  <ReferenceLine x={`S${String(ultimaSemanaAtual).padStart(2, '0')}`} stroke="#0F1829" strokeDasharray="3 3"
                    label={{ value: 'hoje', position: 'top', fill: '#0F1829', fontSize: 10 }} />
                )}
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E5E7EB' }}
                  labelStyle={{ fontSize: 11, fontWeight: 600 }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Ano Atual" fill="#0F1829" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Ano Anterior" fill="#9CA3AF" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Comparativo Anual Comissões */}
      {chartData.length > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 mb-6">
          <h3 className="text-sm font-semibold text-[#111827] mb-4">Comparativo Anual de Comissões</h3>
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <XAxis dataKey="mes" tick={{ fontSize: 11, fill: '#6B7280' }} />
                <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} />
                <Tooltip formatter={(v) => fmt(v)} contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E5E7EB' }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="Ano Atual" stroke="#0F1829" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="Ano Anterior" stroke="#9CA3AF" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="5 5" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Projeção de Faturação a 31 Dez */}
      {projChartData.length > 0 && (
        <div data-testid="chart-projecao" className="bg-white border border-[#E5E7EB] rounded-lg p-5 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-[#111827]">Projeção de Faturação — 31 Dez {currentYear}</h3>
              <p className="text-xs text-[#6B7280] mt-0.5">Dois métodos: taxa da rede global vs soma das projeções individuais de cada consultor</p>
            </div>
            <div className="flex items-center gap-5 text-right">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[#6B7280]">Proj. Rede</p>
                <p className="text-sm font-bold text-[#E65100]">{fmt(projectedTotal)}</p>
                <p className={`text-[10px] font-semibold ${growthPct >= 0 ? 'text-[#00C853]' : 'text-[#FF2B2B]'}`}>
                  {growthPct >= 0 ? '+' : ''}{growthPct.toFixed(1)}%
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[#6B7280]">Proj. Individual</p>
                <p className="text-sm font-bold text-[#978D78]">{fmt(projectedIndivTotal)}</p>
                <p className={`text-[10px] font-semibold ${growthPctIndiv >= 0 ? 'text-[#00C853]' : 'text-[#FF2B2B]'}`}>
                  {growthPctIndiv >= 0 ? '+' : ''}{growthPctIndiv.toFixed(1)}%
                </p>
              </div>
            </div>
          </div>
          <div className="h-[320px]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={projChartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#6B7280' }} interval={1} angle={-45} textAnchor="end" height={55} />
                <YAxis tick={{ fontSize: 10, fill: '#6B7280' }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                <Tooltip formatter={(v) => fmt(v)} contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E5E7EB' }}
                  labelStyle={{ fontSize: 11, fontWeight: 600 }} />
                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                <Line type="monotone" dataKey="Ano Anterior" stroke="#9CA3AF" strokeWidth={1.5} dot={false} strokeDasharray="4 4" />
                <Line type="monotone" dataKey="Real" stroke="#0F1829" strokeWidth={2.5} dot={{ r: 2.5, fill: '#0F1829' }} />
                <Line type="monotone" dataKey="Proj. Rede" stroke="#E65100" strokeWidth={2} dot={{ r: 2, fill: '#E65100' }} strokeDasharray="6 3" />
                <Line type="monotone" dataKey="Proj. Individual" stroke="#978D78" strokeWidth={2} dot={{ r: 2, fill: '#978D78' }} strokeDasharray="3 3" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center gap-5 mt-3 pt-3 border-t border-[#F3F4F6] flex-wrap">
            <div className="flex items-center gap-2">
              <span className="w-3 h-0.5 inline-block" style={{borderTop:'2px dashed #9CA3AF'}}></span>
              <span className="text-[10px] text-[#6B7280]">{currentYear - 1}: {fmt(lastYearTotal)}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-0.5 bg-[#0F1829] inline-block"></span>
              <span className="text-[10px] text-[#6B7280]">Real {currentYear}: {fmt(actualYTD)}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-0.5 inline-block" style={{borderTop:'2px dashed #E65100'}}></span>
              <span className="text-[10px] text-[#E65100] font-medium">Proj. Rede: {fmt(projectedTotal)}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-0.5 inline-block" style={{borderTop:'2px dashed #978D78'}}></span>
              <span className="text-[10px] text-[#978D78] font-medium">Proj. Individual: {fmt(projectedIndivTotal)}</span>
            </div>
            {projectedIndivTotal > 0 && projectedTotal > 0 && (
              <span className="text-[10px] text-[#6B7280] ml-auto">
                Diferença: {fmt(projectedIndivTotal - projectedTotal)} ({((projectedIndivTotal / projectedTotal - 1) * 100).toFixed(1)}%)
              </span>
            )}
          </div>
        </div>
      )}

      {/* Comparativo Anual Faturação */}
      {fatChartData.length > 0 && (
        <div data-testid="chart-faturacao" className="bg-white border border-[#E5E7EB] rounded-lg p-5 mb-6">
          <h3 className="text-sm font-semibold text-[#111827] mb-4">Comparativo Mensal de Faturação da Rede</h3>
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={fatChartData}>
                <XAxis dataKey="mes" tick={{ fontSize: 11, fill: '#6B7280' }} />
                <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} />
                <Tooltip formatter={(v) => fmt(v)} contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E5E7EB' }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="Ano Atual" stroke="#00C853" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="Ano Anterior" stroke="#9CA3AF" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="5 5" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Comparativo Mensal de Faturação Pessoal (Consumo Pessoal + Venda a Clientes) */}
      {fatPessoalChartData.length > 0 && (
        <div data-testid="chart-faturacao-pessoal" className="bg-white border border-[#E5E7EB] rounded-lg p-5 mb-6">
          <div className="flex items-baseline justify-between mb-4 gap-3 flex-wrap">
            <div>
              <h3 className="text-sm font-semibold text-[#111827]">Comparativo Mensal de Faturação Pessoal</h3>
              <p className="text-[11px] text-[#6B7280] mt-0.5">Consumo Pessoal + Venda a Clientes (fórmula dos Comparativos Mensais)</p>
            </div>
          </div>
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={fatPessoalChartData}>
                <XAxis dataKey="mes" tick={{ fontSize: 11, fill: '#6B7280' }} />
                <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} />
                <Tooltip formatter={(v) => fmt(v)} contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E5E7EB' }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="Ano Atual" stroke="#002480" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="Ano Anterior" stroke="#9CA3AF" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="5 5" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4">
        {/* Birthdays */}
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
          <h3 className="text-sm font-semibold text-[#111827] mb-4">Aniversários Hoje {birthdays.length > 0 && <span className="text-[#6B7280] font-normal">({birthdays.length})</span>}</h3>
          {birthdays.length > 0 ? (
            <>
            <div className="space-y-2">
              {birthdays.slice((bdPage - 1) * BD_PER_PAGE, bdPage * BD_PER_PAGE).map((b, i) => {
                const absIdx = (bdPage - 1) * BD_PER_PAGE + i;
                return (
                <div key={`bd-${b.ID_Cliente || absIdx}`} data-testid={`birthday-${absIdx}`} className="py-2.5 border-b border-[#F3F4F6] last:border-0">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <span className="text-sm font-medium text-[#111827]"><PersonLink id={b.ID_Cliente} name={b.NomeCompleto} /></span>
                      {b.Idade != null && <span className="text-xs text-[#6B7280] ml-2">({b.Idade} anos)</span>}
                      <span className={`ml-2 text-[9px] uppercase tracking-[0.1em] font-bold px-1.5 py-0.5 rounded ${b.Source === 'D' ? 'bg-[#0F1829] text-white' : 'bg-[#E6E1D6] text-[#4B5563]'}`}
                        title={b.Source === 'D' ? 'Cliente directo' : 'Cliente da rede'}>
                        {b.Source === 'D' ? 'Directo' : 'Rede'}
                      </span>
                    </div>
                    <span className="text-[10px] text-[#6B7280] whitespace-nowrap">{b.DataNascimento}</span>
                  </div>
                  {b.NomeLigadoA && b.Source === 'R' && (
                    <div className="text-[11px] text-[#6B7280] mt-1" data-testid={`birthday-linked-${absIdx}`}>
                      Ligado a <PersonLink id={b.IdLigadoA} name={b.NomeLigadoA} className="text-[#0F1829] hover:underline" />
                    </div>
                  )}
                  <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                    {b.Telefone && (
                      <span className="inline-flex items-center gap-1.5">
                        <a href={`tel:${b.Telefone}`} data-testid={`birthday-call-${absIdx}`}
                          className="inline-flex items-center gap-1 text-xs font-medium text-[#0F1829] hover:underline">
                          <Phone size={12} weight="bold" /> {b.Telefone}
                        </a>
                        <WhatsAppLink
                          phone={b.Telefone}
                          message={`Olá ${(b.Nome || '').split(' ')[0] || ''}! Parabéns pelo seu aniversário 🎉. Toda a equipa da IHTP deseja-lhe um dia muito especial!`}
                          firstName={(b.Nome || '').split(' ')[0] || ''}
                          size={11} className="w-5 h-5"
                        />
                      </span>
                    )}
                    {b.Email && (
                      <a href={`mailto:${b.Email}`} data-testid={`birthday-email-${absIdx}`}
                        className="inline-flex items-center gap-1 text-xs text-[#6B7280] hover:text-[#0F1829] hover:underline">
                        <Envelope size={12} /> {b.Email}
                      </a>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
            {birthdays.length > BD_PER_PAGE && (() => {
              const totalPages = Math.ceil(birthdays.length / BD_PER_PAGE);
              return (
                <div className="flex items-center justify-center gap-2 mt-4" data-testid="birthdays-pagination">
                  <button onClick={() => setBdPage(p => Math.max(1, p - 1))} disabled={bdPage <= 1}
                    data-testid="birthdays-prev"
                    className="p-1.5 rounded border border-[#E5E7EB] disabled:opacity-30 hover:bg-[#F9FAFB] transition-colors">
                    <CaretLeft size={14} />
                  </button>
                  <span className="text-xs text-[#6B7280] font-mono px-2" data-testid="birthdays-page-info">
                    Pág {bdPage} / {totalPages}
                  </span>
                  <button onClick={() => setBdPage(p => Math.min(totalPages, p + 1))} disabled={bdPage >= totalPages}
                    data-testid="birthdays-next"
                    className="p-1.5 rounded border border-[#E5E7EB] disabled:opacity-30 hover:bg-[#F9FAFB] transition-colors">
                    <CaretRight size={14} />
                  </button>
                </div>
              );
            })()}
            </>
          ) : <p className="text-sm text-[#6B7280]">Sem aniversários hoje</p>}
        </div>
      </div>

      {/* Downlines Expiring */}
      <div data-testid="downlines-expiring" className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden mt-4">
        <div className="px-5 py-3 border-b border-[#E5E7EB] bg-[#F9FAFB]">
          <h3 className="text-sm font-semibold text-[#111827]">Downlines com contrato a expirar nos próximos 90 dias</h3>
        </div>
        {downlines.length > 0 ? (
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
              <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-4 py-2.5">Nome</th>
              <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2.5">Tipo</th>
              <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2.5">Data Validade</th>
              <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] px-3 py-2.5">Contacto</th>
            </tr></thead>
            <tbody>
              {downlines.map((d, i) => (
                <tr key={`dl-${d.ID_Cliente || i}-${d.DataValidade || ''}`} data-testid={`downline-${i}`} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB] transition-colors">
                  <td className="px-4 py-2.5 text-sm font-medium text-[#111827]"><PersonLink id={d.ID_Cliente} name={d.NomeCompleto} /></td>
                  <td className="px-3 py-2.5"><span className="text-[9px] uppercase tracking-[0.15em] font-semibold px-1.5 py-0.5 rounded border bg-blue-50 text-[#0F1829] border-[#0F1829]/20">{d.TipoCliente}</span></td>
                  <td className="px-3 py-2.5 text-sm text-[#111827]">{d.DataValidade}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      {d.Telefone && <a href={`tel:${d.Telefone}`} className="inline-flex items-center gap-1 text-xs text-[#0F1829] hover:underline"><Phone size={11} weight="bold" />{d.Telefone}</a>}
                      {d.Email && <a href={`mailto:${d.Email}`} className="inline-flex items-center gap-1 text-xs text-[#6B7280] hover:text-[#0F1829] hover:underline"><Envelope size={11} />{d.Email}</a>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        ) : <p className="text-center py-6 text-sm text-[#6B7280]">Sem downlines a expirar</p>}
      </div>

      {/* ── Row: Próximos Cursos + Últimas Inscrições + Registos Recentes ── */}
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mt-6">
      {/* Próximos Cursos */}
      <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden shadow-sm" data-testid="next-courses">
        <div className="px-5 py-3 border-b border-[#E5E7EB] bg-[#F9FAFB]">
          <h3 className="text-sm font-semibold text-[#111827]">Próximos Cursos</h3>
        </div>
        {nextCourses.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-[#F9FAFB] text-[#6B7280] text-xs uppercase tracking-wide">
                <th className="px-3 py-2 text-left font-medium">Curso</th>
                <th className="px-3 py-2 text-left font-medium w-24">Data</th>
                <th className="px-3 py-2 text-center font-medium w-20">Inscritos</th>
                <th className="px-3 py-2 text-center font-medium w-20">Pagos</th>
              </tr></thead>
              <tbody className="divide-y divide-[#E5E7EB]">
                {nextCourses.map((c, i) => (
                  <tr key={`nc-${c.ID_Curso || c.Curso || i}-${c.DataInicio || ''}`} className="hover:bg-[#F9FAFB]">
                    <td className="px-3 py-2 text-[#111827] font-medium">{c.Curso}</td>
                    <td className="px-3 py-2 text-[#6B7280]">{new Date(c.DataInicio).toLocaleDateString('pt-PT')}</td>
                    <td className="px-3 py-2 text-center font-semibold text-[#0F1829]">{c.NInscritos}</td>
                    <td className="px-3 py-2 text-center font-semibold text-green-600">{c.NPagos}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="text-center py-6 text-sm text-[#6B7280]">Sem cursos agendados</p>}
      </div>

      {/* Últimas Inscrições (cursos/eventos) */}
      <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden shadow-sm" data-testid="latest-inscriptions">
        <div className="px-5 py-3 border-b border-[#E5E7EB] bg-[#F9FAFB]">
          <h3 className="text-sm font-semibold text-[#111827]">Últimas Inscrições</h3>
        </div>
        {latestInscriptions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-[#F9FAFB] text-[#6B7280] text-xs uppercase tracking-wide">
                <th className="px-3 py-2 text-left font-medium">Curso / Evento</th>
                <th className="px-3 py-2 text-left font-medium">Nome Encomenda</th>
              </tr></thead>
              <tbody className="divide-y divide-[#E5E7EB]">
                {latestInscriptions.slice(0, 20).map((r, i) => (
                  <tr key={`insc-${r.ID_Encomenda || ''}-${r.cursoEvento || ''}-${i}`} className="hover:bg-[#F9FAFB]">
                    <td className="px-3 py-2 text-[#111827] font-medium">{r.cursoEvento}</td>
                    <td className="px-3 py-2 text-[#374151]">{r.nomeEncomenda}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="text-center py-6 text-sm text-[#6B7280]">Sem inscrições recentes</p>}
      </div>

      {/* Registos Mais Recentes */}
      <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden shadow-sm" data-testid="latest-registers">
        <div className="px-5 py-3 border-b border-[#E5E7EB] bg-[#F9FAFB]">
          <h3 className="text-sm font-semibold text-[#111827]">Registos Mais Recentes (Rede)</h3>
        </div>
        {latestRegisters.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-[#F9FAFB] text-[#6B7280] text-xs uppercase tracking-wide">
                <th className="px-3 py-2 text-left font-medium">Nome</th>
                <th className="px-3 py-2 text-left font-medium w-28">Data Registo</th>
                <th className="px-3 py-2 text-left font-medium">Consultor</th>
              </tr></thead>
              <tbody className="divide-y divide-[#E5E7EB]">
                {latestRegisters.slice(0, 20).map((r, i) => (
                  <tr key={`lr-${r.ID_Cliente || i}-${r.DataRegisto || ''}`} className="hover:bg-[#F9FAFB]">
                    <td className="px-3 py-2 text-[#111827] font-medium"><PersonLink id={r.ID_Cliente} name={r.NomeCompleto} /></td>
                    <td className="px-3 py-2 text-[#6B7280]">{r.DataRegisto ? new Date(r.DataRegisto).toLocaleDateString('pt-PT') : '-'}</td>
                    <td className="px-3 py-2 text-[#374151]">
                      {r.nomeConsultor ? (
                        <span className="inline-flex items-center gap-2">
                          {r.ID_Consultor ? (
                            <img
                              src={`${""}/api/profile/photo/${r.ID_Consultor}?uid=${r.ID_Consultor}`}
                              alt={r.nomeConsultor}
                              data-testid={`consultor-avatar-${i}`}
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              className="w-6 h-6 rounded-full object-cover border border-[#E5E7EB]"
                            />
                          ) : null}
                          <span>{r.nomeConsultor}</span>
                        </span>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="text-center py-6 text-sm text-[#6B7280]">Sem registos recentes</p>}
      </div>
    </div>
  </div>
  );
}
